






































import { computed, defineComponent, onBeforeUnmount, onMounted, ref } from "@vue/composition-api";
import { isIOS, isSafari } from "@/lib/browserUtils";
import { getElement } from "@/lib/elementHelpers";
import BaseButton from "@/components/BaseButton";

export default defineComponent({
  name: "InfiniteScroll",
  components: { BaseButton },
  props: {
    items: { type: Array as () => any[] },
    startPosition: { type: Number, default: 0 },
    pageSize: { type: Number, default: 20 },
    enable: { type: Boolean, required: true },
  },
  setup(props, { root, emit, listeners }) {
    const loadingNext = ref(false);
    const loadingPrev = ref(false);
    const currentPosition = ref(0);
    const endPosition = ref(0);
    const isScrolling = ref(false);
    const observerPrev = ref<HTMLElement>();
    const observerNext = ref<HTMLElement>();
    let timeout = 0;

    const itemsToPrint = computed(() => props.items?.slice(currentPosition.value, endPosition.value));

    const allPrinted = computed(() => {
      if (!props.items) return true;
      return props.items.length === itemsToPrint.value?.length;
    });

    const showPrevButton = computed(
      () => (props.enable && !!listeners.prev) || currentPosition.value > 0 || loadingPrev.value
    );

    const showNextButton = computed(
      () => (props.enable && !!listeners.next) || endPosition.value < (props.items?.length ?? 0) || loadingNext.value
    );

    const loadNext = async () => {
      loadingNext.value = true;
      if (endPosition.value === (props.items?.length ?? 0)) {
        emit("next", () => {
          loadingNext.value = false;
          endPosition.value = Math.min(endPosition.value + props.pageSize, props.items?.length ?? 0);
        });
        return;
      }
      await new Promise(r => setTimeout(r, 50));
      endPosition.value = Math.min(endPosition.value + props.pageSize, props.items?.length ?? 0);
      loadingNext.value = false;
    };

    const loadPrev = async () => {
      loadingPrev.value = true;
      if (allPrinted.value) {
        emit("prev", () => (loadingPrev.value = false));
        return;
      }

      const initial = document.documentElement.scrollHeight;
      if (isSafari() || isIOS()) {
        await new Promise<void>(r => {
          const i = setInterval(() => {
            if (!isScrolling.value) {
              clearInterval(i);
              r();
            }
          }, 10);
        });
      }
      await new Promise(r => setTimeout(r, 50));
      currentPosition.value = Math.max(currentPosition.value - props.pageSize, 0);
      loadingPrev.value = false;
      await root.$nextTick();
      if (Math.abs(document.documentElement.scrollHeight - initial) > 0) {
        window.scrollBy({
          top: document.documentElement.scrollHeight - initial,
          behavior: "auto",
        });
      }
    };

    const onScroll = () => {
      clearTimeout(timeout);
      isScrolling.value = true;
      timeout = (setTimeout(() => {
        isScrolling.value = false;
      }, 150) as any) as number;
    };

    const onObserve = (entries: IntersectionObserverEntry[]) => {
      if (allPrinted.value && !props.enable) return;
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        const fromTop = entry.target === observerPrev.value;
        if (fromTop) {
          loadPrev();
        } else loadNext();
      });
    };

    const observer: IntersectionObserver = new IntersectionObserver(onObserve, {
      rootMargin: "20% 0px 20% 0px",
      threshold: 1,
    });

    const countInitialPosition = () => {
      const length = props.items?.length ?? 0;
      currentPosition.value = Math.max(Math.min(props.startPosition, length - props.pageSize), 0);
      endPosition.value = Math.min(currentPosition.value + props.pageSize, length);
    };

    onMounted(() => {
      countInitialPosition();
      observerPrev.value && observer.observe(getElement(observerPrev.value));
      observerNext.value && observer.observe(getElement(observerNext.value));
      window.addEventListener("scroll", onScroll);
    });

    onBeforeUnmount(() => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
    });

    return {
      showPrevButton,
      showNextButton,
      loadingPrev,
      loadingNext,
      itemsToPrint,
      observerPrev,
      observerNext,
      loadPrev,
      loadNext,
    };
  },
});
